import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { message } from 'antd';
import { ArrowLeftIcon, DocumentMagnifyingGlassIcon } from '@heroicons/react/24/outline';
import releaseNoteApi from '../../services/releaseNoteApi';
import { PageHeader, TWButton, EmptyState } from '../../components/ui';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import { isNotFound, FEED_PATH } from './release-note-model';
import { markReleaseNotesSeen } from './release-notes-seen';
import ReleaseNoteMeta from './components/ReleaseNoteMeta';
import ReleaseNoteBlocks from './components/ReleaseNoteBlocks';
import './release-notes.css';

const SHELL_CLASS =
  'release-note-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

const DetailSkeleton = () => (
  <div className="grid gap-x-12 gap-y-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]" aria-hidden="true">
    <div className="space-y-3">
      <SkeletonBlock className="h-3 w-24" />
      <SkeletonBlock className="h-9 w-full" />
      <SkeletonBlock className="h-9 w-3/4" />
      <div className="flex items-center gap-3.5">
        <SkeletonBlock className="h-14 w-14 !rounded-full" />
        <div className="space-y-1.5">
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-3 w-20" />
        </div>
      </div>
    </div>
    <div className="space-y-3">
      <SkeletonBlock className="h-5 w-full" />
      <SkeletonBlock className="h-4 w-11/12" />
      <SkeletonBlock className="h-4 w-full" />
      <SkeletonBlock className="aspect-video w-full !rounded-lg" />
    </div>
  </div>
);

// One published note (spec §5). `Get?slug=` answers 404 for a draft or an
// unknown slug — the body is ProblemDetails, so the status decides.
const ReleaseNote = () => {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setMissing(false);
    setFailed(false);
    try {
      const res = await releaseNoteApi.getBySlug(slug);
      // A slower answer for a slug we already left must not overwrite this one.
      if (id !== requestId.current) return;
      const detail = res.data?.data || null;
      setNote(detail);
      // A permalink to the newest note counts as having seen it (spec §6).
      if (detail?.publishedat) markReleaseNotesSeen(detail.publishedat);
    } catch (err) {
      if (id !== requestId.current) return;
      setNote(null);
      if (isNotFound(err)) setMissing(true);
      else {
        setFailed(true);
        message.error("Couldn't load this update");
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <PageHeader backTo={FEED_PATH} backLabel="What's new" />

        {loading ? (
          <DetailSkeleton />
        ) : missing ? (
          <EmptyState
            variant="dashed"
            icon={DocumentMagnifyingGlassIcon}
            title="This update isn't available"
            description="It may have been unpublished, or the link is wrong."
            action={
              <Link
                to={FEED_PATH}
                className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400"
              >
                Back to What's new
              </Link>
            }
          />
        ) : failed || !note ? (
          <EmptyState
            variant="dashed"
            title="Couldn't load this update"
            description="Check your connection and try again."
            action={<TWButton variant="primary" label="Retry" onClick={load} />}
          />
        ) : (
          <article className="grid gap-x-12 gap-y-6 pt-2 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* The window scrolls under the layout's sticky h-[8vh] top bar. */}
            <ReleaseNoteMeta
              note={note}
              titleAs="h1"
              publisherCaption="Thisway team"
              className="self-start lg:sticky lg:top-[calc(8vh+1.5rem)]"
            />
            <div className="flex min-w-0 max-w-[68ch] flex-col gap-5">
              {note.summary ? (
                <p className="text-lg leading-relaxed text-slate-900 dark:text-slate-100">{note.summary}</p>
              ) : null}
              <ReleaseNoteBlocks blocks={note.blocks} />
              <div className="mt-4 border-t border-slate-200 pt-6 dark:border-slate-700">
                <Link
                  to={FEED_PATH}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
                >
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden />
                  All updates
                </Link>
              </div>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default ReleaseNote;
